/**
 * HULASI HEADER — hulasi-header.js
 * Version: 4.0.0
 * Theme: Dawn 15.2.0 (Online Store 2.0)
 * Section: sections/header.liquid
 *
 * Features:
 *  1. Sticky glass effect on scroll
 *  2. Desktop navigation placement — enforced via JS as fallback
 *  3. Mega menu — hover intent, keyboard, ESC, click-outside
 *  4. Mobile drawer — open/close, iOS scroll lock, focus trap
 *  5. Meteor grid protection — prevents 3rd-party nav injection
 *
 * Developer notes:
 *  - Zero console.log / console.warn in production
 *  - All selectors use data-attributes or id-prefix matching
 *    to survive Shopify section ID changes
 *  - initSticky compacts the header only after the page scrolls
 */

(function () {
  'use strict';

  /* =========================================================
     CONSTANTS
     ========================================================= */

  var SEL = {
    section: '.hulasi-header-section',
    sticky: '[id^="HulasiSticky-"]',
    stickySpacer: '[id^="HulasiStickySpacer-"]',
    megaTrigger: '[data-mega-trigger]',
    megaPanel: '[data-mega-panel]',
    menuToggle: '[id^="HulasiMenuToggle-"]',
    drawer: '[id^="HulasiDrawer-"]',
    drawerClose: '[id^="HulasiDrawerClose-"]',
    overlay: '[id^="HulasiOverlay-"]'
  };

  var CLS = {
    scrolled: 'is-scrolled',
    fixed: 'is-fixed',
    megaActive: 'is-active',
    drawerOpen: 'is-open',
    bodyLock: 'hulasi-drawer-open'
  };

  var MEGA_OPEN_DELAY = 80;
  var MEGA_CLOSE_DELAY = 180;

  /* =========================================================
     UTILITY
     ========================================================= */

  function isDesktop() {
    return window.innerWidth > 989;
  }

  /* =========================================================
     1. STICKY HEADER
     ========================================================= */

  var _stickyEl = null;
  var _stickySpacer = null;
  var _stickyStart = 0;
  var _stickyScrollHandler = null;
  var _stickyResizeHandler = null;
  var _stickySizeObserver = null;

  function updateSticky() {
    if (!_stickyEl || !_stickySpacer) return;

    var shouldFix = window.scrollY > _stickyStart;

    if (shouldFix) {
      _stickyEl.classList.add(CLS.fixed, CLS.scrolled);
      _stickyEl.style.setProperty('position', 'fixed', 'important');
      _stickyEl.style.setProperty('top', '0px', 'important');
      _stickyEl.style.setProperty('z-index', '1000', 'important');
      _stickySpacer.style.height = _stickyEl.offsetHeight + 'px';
    } else {
      _stickyEl.classList.remove(CLS.fixed, CLS.scrolled);
      _stickyEl.style.setProperty('position', 'sticky', 'important');
      _stickyEl.style.setProperty('top', '0px', 'important');
      _stickyEl.style.setProperty('z-index', '1000', 'important');
      _stickySpacer.style.height = '0px';
    }
  }

  function measureStickyStart() {
    if (!_stickyEl || !_stickySpacer) return;

    _stickyEl.classList.remove(CLS.fixed, CLS.scrolled);
    _stickyEl.style.setProperty('position', 'sticky', 'important');
    _stickySpacer.style.height = '0px';
    _stickyStart = _stickyEl.getBoundingClientRect().top + window.scrollY;
    updateSticky();
  }

  function initSticky() {
    _stickyEl = document.querySelector(SEL.sticky);
    _stickySpacer = document.querySelector(SEL.stickySpacer);
    if (!_stickyEl || !_stickySpacer) return;

    if (_stickyScrollHandler) {
      window.removeEventListener('scroll', _stickyScrollHandler);
    }
    _stickyScrollHandler = updateSticky;
    window.addEventListener('scroll', _stickyScrollHandler, { passive: true });

    if (_stickyResizeHandler) {
      window.removeEventListener('resize', _stickyResizeHandler);
    }
    var resizeTimer;
    _stickyResizeHandler = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measureStickyStart, 120);
    };
    window.addEventListener('resize', _stickyResizeHandler, { passive: true });

    if (_stickySizeObserver) _stickySizeObserver.disconnect();
    if ('ResizeObserver' in window) {
      _stickySizeObserver = new ResizeObserver(function () {
        if (_stickyEl.classList.contains(CLS.fixed)) {
          _stickySpacer.style.height = _stickyEl.offsetHeight + 'px';
        }
      });
      _stickySizeObserver.observe(_stickyEl);
    }

    measureStickyStart();
    window.addEventListener('load', measureStickyStart, { once: true });
  }

  /* =========================================================
     2. NAV DISTRIBUTION
     =========================================================
     Keeps menu labels together while allowing the nav bar itself
     to remain full width. Inline styles override legacy theme rules.
     ========================================================= */

  function initNavDistribution() {
    var navEl = document.querySelector('.hulasi-nav');
    if (!navEl) return;

    var inner = navEl.querySelector('.hulasi-nav__inner');
    var list = navEl.querySelector('.hulasi-nav__list');
    if (!inner || !list) return;
    var singleRow = window.innerWidth >= 1200;

    /* Full width inner container */
    inner.style.setProperty('width', '100%', 'important');
    inner.style.setProperty('max-width', '100%', 'important');
    inner.style.setProperty('display', 'flex', 'important');
    inner.style.setProperty('align-items', 'center', 'important');
    inner.style.setProperty('justify-content', singleRow ? 'center' : 'flex-start', 'important');
    inner.style.setProperty('padding-left', singleRow ? '0' : 'clamp(180px, 17vw, 250px)', 'important');
    inner.style.setProperty('padding-right', singleRow ? '0' : '28px', 'important');

    /* Keep the content-sized list without changing menu items. */
    list.style.setProperty('display', 'flex', 'important');
    list.style.setProperty('justify-content', 'center', 'important');
    list.style.setProperty('align-items', 'center', 'important');
    list.style.setProperty('gap', singleRow ? 'clamp(4px, 0.5vw, 10px)' : 'clamp(12px, 2vw, 32px)', 'important');
    list.style.setProperty('flex', '0 1 auto', 'important');
    list.style.setProperty('width', 'auto', 'important');

    /* Items take only the width their labels need. */
    var items = list.querySelectorAll('.hulasi-nav__item');
    items.forEach(function (item) {
      item.style.setProperty('flex', '0 0 auto', 'important');
      item.style.setProperty('justify-content', 'center', 'important');
      item.style.setProperty('display', 'flex', 'important');
    });
  }

  /* ─────────────────────────────────────────────
   * FIX: Ensure all [data-mega-trigger] panels open
   * even when Liquid _is_* flag miss the item.
   * We re-scan after DOM ready and mark any li that
   * has a [data-mega-panel] child as a trigger.
   * ───────────────────────────────────────────── */
  function ensureMegaTriggers() {
    document.querySelectorAll('.hulasi-nav__item').forEach(function (item) {
      var panel = item.querySelector('[data-mega-panel]');
      if (panel && !item.hasAttribute('data-mega-trigger')) {
        item.setAttribute('data-mega-trigger', '');
        item.classList.add('hulasi-nav__item--has-mega');
      }
    });
  }

  /* =========================================================
     3. MEGA MENU
     ========================================================= */

  function initMegaMenus() {
    var triggers = document.querySelectorAll(SEL.megaTrigger);
    if (!triggers.length) return;
    if (triggers[0].dataset.megaInit === '1') return;

    var openTimers = new Map();
    var closeTimers = new Map();
    var activeTrigger = null;

    function openMega(trigger) {
      if (activeTrigger && activeTrigger !== trigger) closeMega(activeTrigger);
      var panel = trigger.querySelector(SEL.megaPanel);
      var link = trigger.querySelector('.hulasi-nav__link');
      if (!panel) return;
      trigger.classList.add(CLS.megaActive);
      panel.removeAttribute('hidden');
      if (link) link.setAttribute('aria-expanded', 'true');
      activeTrigger = trigger;
    }

    function closeMega(trigger) {
      var panel = trigger.querySelector(SEL.megaPanel);
      var link = trigger.querySelector('.hulasi-nav__link');
      if (!panel) return;
      trigger.classList.remove(CLS.megaActive);
      panel.setAttribute('hidden', '');
      if (link) link.setAttribute('aria-expanded', 'false');
      if (activeTrigger === trigger) activeTrigger = null;
    }

    function closeAll() {
      triggers.forEach(closeMega);
      openTimers.forEach(clearTimeout);
      closeTimers.forEach(clearTimeout);
      openTimers.clear();
      closeTimers.clear();
    }

    triggers.forEach(function (trigger) {
      trigger.dataset.megaInit = '1';

      trigger.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        var ct = closeTimers.get(trigger);
        if (ct) { clearTimeout(ct); closeTimers.delete(trigger); }
        openTimers.set(trigger, setTimeout(function () {
          openMega(trigger);
          openTimers.delete(trigger);
        }, MEGA_OPEN_DELAY));
      });

      trigger.addEventListener('mouseleave', function () {
        if (!isDesktop()) return;
        var ot = openTimers.get(trigger);
        if (ot) { clearTimeout(ot); openTimers.delete(trigger); }
        closeTimers.set(trigger, setTimeout(function () {
          closeMega(trigger);
          closeTimers.delete(trigger);
        }, MEGA_CLOSE_DELAY));
      });

      var link = trigger.querySelector('.hulasi-nav__link');
      if (link) {
        link.addEventListener('keydown', function (e) {
          if (!isDesktop() || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          trigger.classList.contains(CLS.megaActive)
            ? closeMega(trigger)
            : openMega(trigger);
        });

        // Items without a destination (Admin URL left as Home) toggle their panel on click/tap.
        if (!link.hasAttribute('href')) {
          link.addEventListener('click', function (e) {
            if (!isDesktop()) return;
            e.preventDefault();
            trigger.classList.contains(CLS.megaActive)
              ? closeMega(trigger)
              : openMega(trigger);
          });
        }
      }

      var panel = trigger.querySelector(SEL.megaPanel);
      if (panel) {
        panel.addEventListener('focusout', function () {
          requestAnimationFrame(function () {
            if (!trigger.contains(document.activeElement)) closeMega(trigger);
          });
        });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && activeTrigger) {
        var link = activeTrigger.querySelector('.hulasi-nav__link');
        closeAll();
        if (link) link.focus();
      }
    });

    document.addEventListener('click', function (e) {
      if (activeTrigger && !e.target.closest(SEL.megaTrigger)) closeAll();
    });
  }

  /* =========================================================
     4. MOBILE DRAWER
     ========================================================= */

  function initDrawer() {
    var toggle = document.querySelector(SEL.menuToggle);
    var drawer = document.querySelector(SEL.drawer);
    var closeBtn = document.querySelector(SEL.drawerClose);
    var overlay = document.querySelector(SEL.overlay);

    if (!toggle || !drawer) return;
    if (toggle.dataset.drawerInit === '1') return;
    toggle.dataset.drawerInit = '1';

    /* iOS-safe scroll lock */
    var savedScrollY = 0;

    function lockBody() {
      savedScrollY = window.pageYOffset;
      document.body.style.cssText +=
        ';position:fixed;top:-' + savedScrollY +
        'px;left:0;right:0;';
      document.body.classList.add(CLS.bodyLock);
    }

    function unlockBody() {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.classList.remove(CLS.bodyLock);
      window.scrollTo(0, savedScrollY);
    }

    function isOpen() {
      return drawer.classList.contains(CLS.drawerOpen);
    }

    function openDrawer() {
      lockBody();
      drawer.classList.add(CLS.drawerOpen);
      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      if (overlay) overlay.classList.add(CLS.drawerOpen);
      requestAnimationFrame(function () {
        if (closeBtn) closeBtn.focus();
      });
    }

    function closeDrawer() {
      drawer.classList.remove(CLS.drawerOpen);
      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      if (overlay) overlay.classList.remove(CLS.drawerOpen);
      unlockBody();
      requestAnimationFrame(function () { toggle.focus(); });
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      isOpen() ? closeDrawer() : openDrawer();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function () {
        if (isOpen()) closeDrawer();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) closeDrawer();
    });

    /* Auto-close on desktop resize */
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (isDesktop() && isOpen()) closeDrawer();
      }, 250);
    }, { passive: true });

    /* Focus trap */
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.slice.call(
        drawer.querySelectorAll(
          'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (el) { return el.offsetParent !== null; });

      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  /* =========================================================
     5. METEOR GRID PROTECTION
     ========================================================= */

  function initMeteorProtection() {
    var GS = [
      'ul.product-grid', '.product-grid', 'ul.grid',
      '[id^="Slider-template"] ul', 'slider-component ul',
      '.collection-product-list ul'
    ].join(',');
    var PS = 'li.grid__item,.grid__item,.product-card-wrapper';
    var MS = [
      '#meteor-mobile-nav', '.Meteor-MobileNavigation',
      '.Meteor-MobileMenu', '[id*="meteor-mobile"]',
      '[class*="Meteor-Mobile"]'
    ].join(',');

    var backup = new WeakMap();

    function protect() {
      document.querySelectorAll(GS).forEach(function (grid) {
        if (!backup.has(grid)) {
          var items = grid.querySelectorAll(PS);
          if (items.length) backup.set(grid, { html: grid.innerHTML });
        }
        grid.querySelectorAll(MS).forEach(function (el) { el.remove(); });
        var b = backup.get(grid);
        if (b && (grid.querySelector(MS) || !grid.querySelectorAll(PS).length)) {
          grid.innerHTML = b.html;
        }
        grid.removeAttribute('data-meteor-mounted');
        grid.removeAttribute('data-meteor-platform');
      });
    }

    protect();

    new MutationObserver(function (mutations) {
      var dirty = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (
            node.nodeType === 1 && node.matches &&
            (node.matches(MS) || (node.querySelector && node.querySelector(MS))) &&
            m.target.closest && m.target.closest(GS)
          ) dirty = true;
        });
        if (m.type === 'attributes' && m.target.matches && m.target.matches(GS)) {
          m.target.removeAttribute('data-meteor-mounted');
          m.target.removeAttribute('data-meteor-platform');
        }
      });
      if (dirty) requestAnimationFrame(protect);
    }).observe(document.documentElement, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-meteor-mounted', 'data-meteor-platform']
    });

    [50, 100, 200, 500, 1000, 2000, 3000, 5000].forEach(function (d) {
      setTimeout(protect, d);
    });

    window.addEventListener('load', function () {
      protect();
      setTimeout(protect, 1000);
    }, { once: true });
  }

  /* =========================================================
       6. SHOP BY CATEGORIES — v4
       Switches category, toggles 2-col/3-col, swaps banner.
       Debounced 100ms to prevent flicker.
       ========================================================= */

  function initShopByCategories() {
    var panel = document.querySelector('[data-sbc-panel]');
    if (!panel) return;
    if (panel.dataset.sbcInit === '1') return;
    panel.dataset.sbcInit = '1';

    var body = panel.querySelector('.hulasi-sbc__body');
    var catItems = panel.querySelectorAll('[data-sbc-cat]');
    var subGroups = panel.querySelectorAll('[data-sbc-group]');
    var banners = panel.querySelectorAll('[data-sbc-banner]');
    var middleCol = panel.querySelector('.hulasi-sbc__middle');

    if (!body || !catItems.length) return;

    var debounceTimer = null;

    function activate(index) {
      /* 1 — Left column highlight */
      catItems.forEach(function (el) {
        var on = parseInt(el.dataset.sbcCat, 10) === index;
        el.classList.toggle('hulasi-sbc__cat-item--active', on);
        el.setAttribute('aria-current', on ? 'true' : 'false');
      });

      /* 2 — Does this category have children? */
      var activeItem = panel.querySelector('[data-sbc-cat="' + index + '"]');
      var hasChildren = activeItem
        ? activeItem.getAttribute('data-sbc-has-children') === 'true'
        : false;

      /* 3 — Toggle 3-col layout */
      body.classList.toggle('hulasi-sbc__body--has-children', hasChildren);
      if (middleCol) {
        middleCol.setAttribute('aria-hidden', hasChildren ? 'false' : 'true');
      }

      /* 4 — Show correct sub-group in middle */
      subGroups.forEach(function (el) {
        var on = parseInt(el.dataset.sbcGroup, 10) === index;
        el.classList.toggle('hulasi-sbc__sub-group--active', on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      /* 5 — Swap banner */
      banners.forEach(function (el) {
        var on = parseInt(el.dataset.sbcBanner, 10) === index;
        el.classList.toggle('hulasi-sbc__banner--active', on);
        el.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    }

    /* Attach listeners */
    catItems.forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        clearTimeout(debounceTimer);
        var idx = parseInt(el.dataset.sbcCat, 10);
        debounceTimer = setTimeout(function () {
          activate(idx);
        }, 100);
      });

      el.addEventListener('focus', function () {
        activate(parseInt(el.dataset.sbcCat, 10));
      });
    });

    /* On panel open: always reset to index 0 */
    var megaTrigger = panel.closest('[data-mega-trigger]');
    if (megaTrigger) {
      megaTrigger.addEventListener('mouseenter', function () {
        clearTimeout(debounceTimer);
        activate(0);
      });
    }

    /* Run once on init to set correct initial state */
    activate(0);
  }

  /* =========================================================
     6. DRAWER SCROLL — Sticky Search Bar
     =========================================================
     Hero is first child inside .hulasi-drawer__body.
     As user scrolls, hero goes up and search bar sticks
     to top via CSS position:sticky.
     JS adds .is-sticky class when hero is no longer visible
     for the enhanced glass effect.
     ========================================================= */

  function initDrawerScrollSticky() {
    var body = document.querySelector('.hulasi-drawer__body');
    var hero = document.querySelector('.hulasi-drawer__hero');
    var search = document.querySelector('.hulasi-drawer__search');

    if (!body || !hero || !search) return;

    /* Use IntersectionObserver to detect when hero leaves viewport */
    var heroObserver = new IntersectionObserver(
      function (entries) {
        var isVisible = entries[0].isIntersecting;
        search.classList.toggle('is-sticky', !isVisible);
      },
      {
        root: body,          /* observe within scroll container */
        threshold: 0,
        rootMargin: '0px'
      }
    );

    heroObserver.observe(hero);

    /* Reset on drawer close — remove sticky class */
    var drawerEl = document.querySelector('.hulasi-drawer');
    if (drawerEl) {
      var closeBtn = document.querySelector('.hulasi-drawer__close');
      var overlay = document.querySelector('.hulasi-drawer__overlay');

      function resetDrawerScroll() {
        body.scrollTop = 0;
        search.classList.remove('is-sticky');
      }

      if (closeBtn) closeBtn.addEventListener('click', resetDrawerScroll);
      if (overlay) overlay.addEventListener('click', resetDrawerScroll);
    }
  }

  /* =========================================================
     INIT
     ========================================================= */

  function init() {
    initSticky();
    initNavDistribution();
    ensureMegaTriggers();
    initMegaMenus();
    initDrawer();
    initDrawerScrollSticky();
    initShopByCategories();
    initMeteorProtection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('resize', initNavDistribution, { passive: true });

  /* Shopify Theme Editor — section reload */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector(SEL.section)) {
      init();
    }
  });

})();
